/**
 * @voltxampere/engine — JFET AC small-signal stamp (Sprint 23 B3)
 *
 * Linearised around the DC operating point. JFET admittance:
 *   Y_DG = gm,    Y_DD = gds,    Y_DS = -(gm + gds)
 *   Y_SG = -gm,   Y_SD = -gds,   Y_SS = gm + gds
 *
 * Plus optional Sprint 22 D4-pattern junction caps (Cgs0, Cgd0)
 * that contribute jωC to the gate-source / gate-drain susceptance.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { StampParameterError } from "@voltxampere/shared";
import { getTerminalNetId, getNumericParam } from "../terminal-utils.js";
function readOptionalNumber(params, key, fallback, predicate) {
    const raw = params[key];
    if (typeof raw !== "number" || !Number.isFinite(raw))
        return fallback;
    return predicate(raw) ? raw : fallback;
}
function jfetACStampCommon(ctx, component, polarity) {
    const Beta = getNumericParam(component, "Beta", 1e-4);
    const Vt0 = getNumericParam(component, "Vt0", polarity === "n" ? -2 : 2);
    const Lambda = getNumericParam(component, "Lambda", 0.01);
    if (!(Beta > 0)) {
        throw new StampParameterError(`jfetACStamp: component "${component.id}" has non-positive Beta (got ${String(Beta)})`, {
            meta: {
                stampType: "jfet",
                parameter: "Beta",
                received: Beta,
                expected: "> 0",
                componentId: component.id,
            },
        });
    }
    const gNet = getTerminalNetId(component, "gate");
    const dNet = getTerminalNetId(component, "drain");
    const sNet = getTerminalNetId(component, "source");
    const G = ctx.nodeIndex(gNet);
    const D = ctx.nodeIndex(dNet);
    const S = ctx.nodeIndex(sNet);
    const sign = polarity === "n" ? 1 : -1;
    const vG = ctx.dcNodeVoltages.get(gNet) ?? 0;
    const vD = ctx.dcNodeVoltages.get(dNet) ?? 0;
    const vS = ctx.dcNodeVoltages.get(sNet) ?? 0;
    const Vgs = sign * (vG - vS);
    const Vds = sign * (vD - vS);
    const Vth = sign * Vt0;
    const Vov = Vgs - Vth;
    let gm = 0;
    let gds = 0;
    if (Vov > 0) {
        if (Vds < Vov) {
            const onePlusLVds = 1 + Lambda * Vds;
            gm = 2 * Beta * Vds * onePlusLVds;
            gds = 2 * Beta * (Vov - Vds) * onePlusLVds + Beta * (2 * Vov * Vds - Vds * Vds) * Lambda;
        }
        else {
            const onePlusLVds = 1 + Lambda * Vds;
            gm = 2 * Beta * Vov * onePlusLVds;
            gds = Beta * Vov * Vov * Lambda;
        }
    }
    // Real conductance block (same as DC stamp).
    ctx.addY(D, G, gm, 0);
    ctx.addY(D, D, gds, 0);
    ctx.addY(D, S, -(gm + gds), 0);
    ctx.addY(S, G, -gm, 0);
    ctx.addY(S, D, -gds, 0);
    ctx.addY(S, S, gm + gds, 0);
    // Sprint 22 D4-pattern junction caps (zero-default).
    const Cgs0 = readOptionalNumber(component.params, "Cgs0", 0, (v) => v >= 0);
    const Cgd0 = readOptionalNumber(component.params, "Cgd0", 0, (v) => v >= 0);
    if (Cgs0 > 0) {
        const susceptanceGS = ctx.omega * Cgs0;
        ctx.addY(G, G, 0, susceptanceGS);
        ctx.addY(S, S, 0, susceptanceGS);
        ctx.addY(G, S, 0, -susceptanceGS);
        ctx.addY(S, G, 0, -susceptanceGS);
    }
    if (Cgd0 > 0) {
        const susceptanceGD = ctx.omega * Cgd0;
        ctx.addY(G, G, 0, susceptanceGD);
        ctx.addY(D, D, 0, susceptanceGD);
        ctx.addY(G, D, 0, -susceptanceGD);
        ctx.addY(D, G, 0, -susceptanceGD);
    }
}
export const jfetNACStamp = (ctx, component) => {
    jfetACStampCommon(ctx, component, "n");
};
export const jfetPACStamp = (ctx, component) => {
    jfetACStampCommon(ctx, component, "p");
};
//# sourceMappingURL=jfet.js.map